import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  styleMap,
} from '@lit'

import * as paths from '@nova/paths'
import styles from '@nova/styles'
import { formatMoney, handleize } from '@nova/utils'

import { addToCart, pushToGTMDataLayer } from '@nova/utils'

const crossSellState = {
  initial: 'SHOW_OPTION_SELECTOR',
  submit: 'SHOW_ADD_TO_CART',
  loading: 'SHOW_LOADING',
  error: 'ERROR',
}

class NovaProductCardCrossSell extends LitElement {
  static properties = {
    variantId: {
      type: String,
      attribute: 'variant-id',
    },
    url: {
      type: String,
    },
    available: {
      state: true,
    },
    variantAvailable: {
      type: String,
      attribute: 'variant-available',
    },
    title: {
      type: String,
    },
    image: {
      type: String,
      attribute: 'image',
    },
    imageAlt: {
      type: String,
    },
    compareAtPrice: {
      type: String,
      attribute: 'compare-at-price',
    },
    price: {
      type: String,
    },
    vendor: {
      type: String,
    },
    callout: {
      type: String,
    },
    status: {
      type: String,
      state: true,
    },
    productId: {
      type: String,
      attribute: 'product-id',
    },
    productTitle: {
      type: String,
      attribute: 'product-title',
    },
    category: {
      type: String,
      attribute: 'category',
    },
    variantTitle: {
      type: String,
      attribute: 'variant-title',
    }
  }

  $productCardContentRef = createRef()

  get activePrice() {
    return formatMoney(this.price)
  }

  get activeImage() {
    return `${this.image}&width=480&height=640`
  }

  get activeCompareAtPrice() {
    if (!this.compareAtPrice) return false

    if (this.compareAtPrice > this.price)
      return formatMoney(this.compareAtPrice)
  }

  constructor() {
    super()

    this.available = true
    this.status = crossSellState.initial
  }

  connectedCallback() {
    super.connectedCallback()

    this.available = this.variantAvailable === 'true'
  }

  async handleAddToCart(e) {
    this.status = crossSellState.loading
    try {
      const variantId = e.target.dataset.variantId
      await addToCart({ id: variantId })
      this.status = crossSellState.confirm
    } catch (error) {
      this.status = crossSellState.error
    } finally {
      setTimeout(() => {
        this.status = crossSellState.initial
      }, 2000)
    }
  }

  renderMedia() {
    return html`
      <div class="product-card__media">
        ${
          this.image
            ? html`
            <div class="product-card__image product-card__image--primary">
              <a href=${this.url}>
                <img src=${this.activeImage} alt=${this.title} loading="lazy" />
              </a>
            </div>`
            : nothing
        }
      </div>
    `
  }

  renderAtc() {
    return html`
      <sl-button
        variant="primary:ink"
        ?disabled=${this.available !== true}
        data-variant-id=${this.variantId}
        data-product-id=${this.productId}
        data-product-title=${this.productTitle}
        data-variant-title=${this.variantTitle}
        data-category=${this.category}
        data-vendor=${this.vendor}
        data-price=${this.price}
        ?loading=${this.status === crossSellState.loading}
        @click=${this.handleAddToCart}>
          <span class="hidden flex--md flex--lg">${window.langMessages['products.product.add']}</span>
          <span class="flex hidden--md hidden--lg">
            <sl-icon name="plus-lg"></sl-icon>
          </span>
      </sl-button>
    `
  }

  render() {
    return html`
      <sl-card
        id="product-card-${this.id}"
        class="product-card"
        style="--padding: 0; --border-width: 0; --sl-shadow-x-small: 0;">
        ${this.renderMedia()}
        <div class="product-card__content" ${ref(this.$productCardContentRef)}>
          <div class="product-card__body">
            <header class="product-card__header">
              ${
                this.vendor
                  ? html`<span class="product-card__vendor">${this.vendor}</span>`
                  : ''
              }
              <h4 class="product-card__title">
                <a href=${this.url}>${this.title}</a>
              </h4>
              ${
                this.callout
                  ? html`<span class="product-card__callout">${this.callout}</span>`
                  : ''
              }
            </header>
            <nova-product-card-price
              class="product-card__price"
              .price=${this.activePrice}
              .compareAtPrice=${this.activeCompareAtPrice}>
            </nova-product-card-price>
          </div>
        </div>
        ${this.renderAtc()}
      </sl-card>
    `
  }

  static styles = [
    styles.reset,
    styles.a11y,
    styles.flex,
    styles.helpers,
    styles.typography,
    styles.skeleton,
    css`
      :host {
        display: flex;
        flex-direction: column;
        position: relative;
        container-type: inline-size;
        width: 100%;
        height: 100%;
        position: relative;
      }
      @media screen and (min-width: 750px) and (max-width: 1023px) {
        .flex--md {
          display: flex !important;
        }
      }

      @media screen and (min-width: 1024px) {
        .flex--lg {
          display: flex !important;
        }
      }

      .product-card::part(base) {
        background: transparent;
      }

      .product-card::part(body) {
        display: flex;
        gap: var(--nova-space-sm);
      }

      .product-card__content {
        display: flex;
        align-items: center;
        flex: 1;
      }

      .product-card__body {
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-sm);
      }

      @media screen and (min-width: 768px) {
        .product-card__body {
          gap: var(--nova-space-xsm);
        }
      }

      .product-card__image {
        height: 120px;
        width: 95px;
        overflow: hidden;
        border-radius: 10px;
        background: var(--nova-color-brand-reverse);
      }

      @media screen and (min-width: 1024px) {
        .product-card__image {
          height: 150px;
          width: 120px;
        }
      }

      .product-card__image img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        mix-blend-mode: multiply;
      }

      .product-card__header {
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-xs);
      }

      @media screen and (min-width: 1024px) {
        .product-card__header {
          gap: var(--nova-space-xsm);
        }
      }

      .product-card__vendor {
        font: var(--nova-body-caption);
        line-height: normal;
      }

      .product-card__title {
        font: 700 16px / 130% var(--nova-font-sans-bold);
        font-family: var(--nova-font-sans-bold);
        text-transform: uppercase;
        margin: 0;
      }

      @media screen and (min-width: 1024px) {
        .product-card__title {
          font-size: 18px;
        }
      }

      .product-card__title a {
        font: inherit;
      }

      .product-card__callout {
        font: var(--nova-pullquote);
        line-height: normal;
      }

      .product-card__price {
        font: 400 18px / 90% var(--nova-font-sans);;
      }

      .product-card__price dd {
        margin: 0;
      }

      sl-button {
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: var(--nova-button-radius);
        border-width: 0;
        height: 35px;
        min-height: 35px;
        font-family: var(--nova-font-sans-bold);
        font-size: var(--nova-button-font-size);
        font-weight: 700;
        line-height: 100%;
        text-transform: uppercase;
        padding-inline: var(--nova-button-padding-left-right);
        transition: background-color 0.2s, color 0.2s, border-color 0.2s;
        text-decoration: none;
        margin: auto;
      }
      sl-button::part(base) {
        background-color: transparent;
        display: flex;
        align-items: center;
        min-height: 35px;
        height: 100%;
        pointer-events: none;
      }
      sl-button::part(label) {
        padding: 0;
      }
      sl-button[variant='primary:ink'] {
        background-color: var(--nova-color-brand-charcoal);
        border: none;
        color: var(--nova-color-brand-reverse);
      }
      sl-button[variant='primary:ink']::part(base) {
        border: none;
        color: var(--nova-color-brand-reverse);
      }
      sl-button[variant='primary:ink']:hover {
        background-color: var(--nova-color-brand-taupe);
      }
      sl-button[variant='primary:ink']::part(base):hover {
        color: var(--nova-color-brand-charcoal);
      }
      sl-button[disabled] {
        background-color: var(--nova-color-grey-300);
        color: var(--nova-color-brand-reverse);
      }
      sl-button[disabled]::part(base) {
        color: var(--nova-color-brand-reverse);
      }
      sl-icon {
        font-size: 18px;
        pointer-events: none;
      }
      @media screen and (max-width: 749px) {
        sl-button {
          width: 46px;
          height: 46px;
          border-radius: 50%;
        }
      }
    `,
  ]
}

customElements.define('nova-product-card-cross-sell', NovaProductCardCrossSell)
